import type {ReactElement} from 'react';
import {ControlledList} from '@Components/controlled-list';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE, ControlledListItemSize} from '@Components/controlled-list/controlled-list.types';
import {ShareSocialPostInTeamToggler} from '@Components/share-social-post-in-team-toggler';
import {useSocialMediaWizardPost, useWizardStep} from '@Components/social-media/social-media.hooks';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {GA4EventName} from '@Libraries/ga-events';
import {getPremiumUpsellIconClasses} from '@Libraries/premium-library';
import {shouldShowDraftAndScheduleOptions} from '@Libraries/social-media-wizard-scheduling-library';
import {isUserPremium} from '@Libraries/user.library';
import type {WizardMobileOptionsSheetProps} from './wizard-mobile-options-sheet.types';
import {SAVE_DRAFT_ICON_CLASSES, SCHEDULE_ICON_CLASSES, WIZARD_MOBILE_OPTIONS_SHEET_PANEL_ID, WizardMobileOptionsSheetItemId} from './wizard-mobile-options-sheet.types';
import styles from './wizard-mobile-options-sheet.module.scss';

export function WizardMobileOptionsSheet({onSaveAsDraft, onSchedule}: WizardMobileOptionsSheetProps): ReactElement {
  const wizardStep = useWizardStep();
  const socialPost = useSocialMediaWizardPost();
  const onClose = useDefaultPanelClose(WIZARD_MOBILE_OPTIONS_SHEET_PANEL_ID);

  const canSaveDraftOrSchedule = shouldShowDraftAndScheduleOptions(socialPost, wizardStep);

  const getScheduleIconClasses = (): string => {
    return isUserPremium() ? SCHEDULE_ICON_CLASSES : getPremiumUpsellIconClasses();
  };

  const getDraftAndScheduleOptions = (): ControlledListItemType[] => {
    return [
      {
        id: WizardMobileOptionsSheetItemId.SAVE_DRAFT,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
        text: window.i18next.t('pmwjs_save_draft'),
        iconClasses: SAVE_DRAFT_ICON_CLASSES,
        textClasses: 'ct-neutral-primary',
        listSize: ControlledListItemSize.SMALL,
        onClick: (): void => {
          onSaveAsDraft();
          onClose();
        },
      },
      {
        id: WizardMobileOptionsSheetItemId.SCHEDULE,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
        text: window.i18next.t('pmwjs_schedule'),
        iconClasses: getScheduleIconClasses(),
        textClasses: 'ct-neutral-primary',
        listSize: ControlledListItemSize.SMALL,
        onClick: (): void => {
          onSchedule();
          onClose();
        },
      },
    ];
  };

  const renderDraftAndScheduleOptions = (): ReactElement | null => {
    if (!canSaveDraftOrSchedule) {
      return null;
    }

    return <ControlledList className={styles.optionsList} items={getDraftAndScheduleOptions()} />;
  };

  const renderOptionsDivider = (): ReactElement | null => {
    if (!canSaveDraftOrSchedule) {
      return null;
    }

    return <hr className={styles.optionsDivider} />;
  };

  const renderShareInTeamRow = (): ReactElement | null => {
    if (!isUserPremium() || !socialPost) {
      return null;
    }

    return (
      <>
        {renderOptionsDivider()}
        <div className={styles.shareInTeamRow}>
          <ShareSocialPostInTeamToggler className={styles.shareInTeamToggler} post={socialPost} gaEvent={GA4EventName.COLLABORATION_SOCIALPOST_SHAREINTEAM_WIZARD} />
        </div>
      </>
    );
  };

  return (
    <div className={styles.sheetContent}>
      {renderDraftAndScheduleOptions()}
      {renderShareInTeamRow()}
    </div>
  );
}
